import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import getSiteHealthInvestigations, {
  SiteHealthInvestigationsResponse,
} from "../http/getSiteHealthInvestigations";
import getSiteHealthInvestigationTrace, {
  SiteHealthInvestigationTraceResponse,
} from "../http/getSiteHealthInvestigationTrace";
import postSiteHealthInvestigate, {
  StartHealthInvestigationResponse,
} from "../http/postSiteHealthInvestigate";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

export const useSiteHealthInvestigation = (siteId: string | undefined) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  const investigationsQuery = useQuery<SiteHealthInvestigationsResponse>(
    ["siteHealthInvestigations", siteId],
    async () => {
      const token = await getAuthTokenBlocking(getToken, relogin);
      return getSiteHealthInvestigations(token, siteId!);
    },
    { enabled: !!siteId, staleTime: 60_000 }
  );

  // Kicks off the investigation LRO (202). On success the caller stores the
  // returned operationKey and polls /async_status via useAsyncOperation. A
  // `coalesced: true` response means a run was already in flight - the caller
  // polls the same operationKey rather than treating the repeat as an error.
  // Pass `parentRunId` to continue that run's session as a follow-up.
  const startInvestigation = useMutation<
    StartHealthInvestigationResponse,
    unknown,
    { focus?: string; parentRunId?: string }
  >(async ({ focus, parentRunId }) => {
    const token = await getAuthTokenBlocking(getToken, relogin);
    return postSiteHealthInvestigate(token, siteId!, focus, parentRunId);
  });

  return { investigationsQuery, startInvestigation };
};

// Opt-in investigation AI-trace fetch (only when the operator expands the
// trace disclosure - it can be large). `runId` is read off the persisted row
// (`traceRunId`), so the query only fires once a run exists. Pass
// enabled=false to hold off until the disclosure is open. Modelled on
// useLandlordResearchTrace in useLeads.ts.
export const useSiteHealthInvestigationTrace = (
  siteId: string | null,
  runId: string | null,
  enabled: boolean
) => useInvestigationTraceQuery(siteId, runId, enabled, (d) => d.trace);

// The short step record for a run - what the agent did, one line per step. Shares
// the trace query's key and `select`s the other half of the response, so the two
// disclosures cost ONE request between them rather than one each.
export const useSiteHealthInvestigationSteps = (
  siteId: string | null,
  runId: string | null,
  enabled: boolean
) => useInvestigationTraceQuery(siteId, runId, enabled, (d) => d.steps);

const useInvestigationTraceQuery = <T,>(
  siteId: string | null,
  runId: string | null,
  enabled: boolean,
  select: (data: SiteHealthInvestigationTraceResponse) => T
) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["siteHealthInvestigationTrace", siteId, runId],
    async () =>
      getSiteHealthInvestigationTrace(
        await getAuthTokenBlocking(getToken, relogin),
        siteId!,
        runId!
      ),
    { enabled: !!siteId && !!runId && enabled, select }
  );
};
